import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert, Text } from 'react-native';
import { Button, Card, Title, Paragraph, TextInput, ActivityIndicator, Divider } from 'react-native-paper';
import { getActivityTypes } from '../../../api/activitiesAPI';
import axiosInstance, { API_URL } from '../../../api/axiosConfig';
import { formatCurrency } from '../../../utils/formatUtils';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { useTheme } from 'react-native-paper';
import AsyncStorage from '@react-native-async-storage/async-storage';

const ParkSettingsScreen = ({ navigation }) => {
  const theme = useTheme();
  const [parkSettings, setParkSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedBasePrice, setEditedBasePrice] = useState('');
  const [editedPricePerMinute, setEditedPricePerMinute] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchParkSettings();
  }, []);

  const fetchParkSettings = async () => {
    try {
      setLoading(true);
      const response = await getActivityTypes();
      //console.log('Activity Types:', response);
      
      if (!response.success) {
        throw new Error(response.message || 'Aktivite tipleri getirilemedi');
      }
      
      // Oyun parkı tipi (ID: 1)
      const parkActivityType = response.data.find(type => type.id === 1);
      if (parkActivityType) {
        setParkSettings(parkActivityType);
        // Tam sayı olarak göstermek için parseInt kullanarak ondalık kısmı kaldır
        setEditedBasePrice(parseInt(parkActivityType.base_price).toString());
        setEditedPricePerMinute(parseInt(parkActivityType.price_per_minute).toString());
      } else {
        console.error('Oyun parkı ayarları bulunamadı.');
        Alert.alert('Uyarı', 'Oyun parkı (ID:1) ayarları bulunamadı.');
      }
    } catch (error) {
      console.error('Oyun parkı ayarları getirme hatası:', error);
      Alert.alert(
        'Hata', 
        'Oyun parkı ayarları yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Sadece sayı girilebilmesi için validasyon
  const handleNumberInput = (text, setter) => {
    // Sadece sayıları kabul et, nokta veya virgülü reddet
    const cleanedText = text.replace(/[^0-9]/g, '');
    setter(cleanedText);
  };

  const handleSaveSettings = async () => {
    // Input alanlarının boş olmadığını kontrol et
    if (!editedBasePrice.trim() || !editedPricePerMinute.trim()) {
      Alert.alert('Hata', 'Lütfen tüm fiyat alanlarını doldurun.');
      return;
    }

    // Sayısal değerlere çevir
    const basePrice = parseInt(editedBasePrice, 10);
    const pricePerMinute = parseInt(editedPricePerMinute, 10);

    if (isNaN(basePrice) || isNaN(pricePerMinute)) {
      Alert.alert('Hata', 'Geçersiz fiyat değerleri. Lütfen sayısal değerler girin.');
      return;
    }

    try {
      setUpdating(true);
      
      // API'ye güncelleme isteği gönder
      const response = await axiosInstance.put(`/admin/activity-types/${parkSettings.id}`, {
        base_price: basePrice,
        price_per_minute: pricePerMinute
      });
      
      if (response.data && response.data.success) {
        // Başarılı güncelleme
        setParkSettings({
          ...parkSettings,
          base_price: basePrice,
          price_per_minute: pricePerMinute
        });
        setIsEditing(false);
        Alert.alert('Başarılı', 'Oyun parkı ayarları güncellendi.');
      } else {
        throw new Error('Ayarlar güncellenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Ayarları güncelleme hatası:', error);
      Alert.alert(
        'Hata', 
        'Ayarlar güncellenirken bir sorun oluştu. Lütfen daha sonra tekrar deneyin.'
      );
    } finally {
      setUpdating(false);
    }
  };

  const startEditing = () => {
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    // Tam sayı olarak göstermek için parseInt kullanarak ondalık kısmı kaldır
    setEditedBasePrice(parseInt(parkSettings.base_price).toString());
    setEditedPricePerMinute(parseInt(parkSettings.price_per_minute).toString());
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Ayarlar yükleniyor...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.headerContainer}>
            <MaterialCommunityIcons name="cog" size={24} color="rgb(125, 76, 236)" />
            <Title style={styles.headerTitle}>Oyun Parkı Fiyat Ayarları</Title>
          </View>
          
          <Divider style={styles.divider} />
          
          {parkSettings ? (
            <View style={styles.settingsContainer}>
              <View style={styles.settingRow}>
                <Text style={styles.settingLabel}>Aktivite Adı:</Text>
                <Text style={styles.settingValue}>{parkSettings.name}</Text>
              </View>
              
              {isEditing ? (
                <>
                  <View style={styles.inputRow}>
                    <Text style={styles.settingLabel}>Giriş Ücreti:</Text>
                    <TextInput
                      label="Giriş Ücreti (TL)"
                      value={editedBasePrice}
                      onChangeText={(text) => handleNumberInput(text, setEditedBasePrice)}
                      keyboardType="numeric"
                      mode="outlined"
                      style={styles.input}
                      disabled={updating}
                      outlineColor="rgb(125, 76, 236)"
                      activeOutlineColor="rgb(125, 76, 236)"
                    />
                  </View>
                  
                  <View style={styles.inputRow}>
                    <Text style={styles.settingLabel}>Dakika Ücreti:</Text>
                    <TextInput
                      label="Dakika Ücreti (TL)"
                      value={editedPricePerMinute}
                      onChangeText={(text) => handleNumberInput(text, setEditedPricePerMinute)}
                      keyboardType="numeric"
                      mode="outlined"
                      style={styles.input}
                      disabled={updating}
                      outlineColor="rgb(125, 76, 236)"
                      activeOutlineColor="rgb(125, 76, 236)"
                    />
                  </View>
                  
                  <View style={styles.buttonContainer}>
                    <Button
                      mode="contained"
                      icon="content-save"
                      onPress={handleSaveSettings}
                      style={[styles.button, {backgroundColor: 'rgb(125, 76, 236)'}]}
                      loading={updating}
                      disabled={updating}
                      labelStyle={{color: 'white', fontWeight: 'bold'}}
                    >
                      <Text style={{color: 'white'}}>Kaydet</Text>
                    </Button>
                    
                    <Button
                      mode="outlined"
                      icon="cancel"
                      onPress={cancelEditing}
                      style={[styles.button, {borderColor: 'rgb(125, 76, 236)'}]}
                      disabled={updating}
                      textColor="rgb(125, 76, 236)"
                    >
                      İptal
                    </Button>
                  </View>
                </>
              ) : (
                <>
                  <View style={styles.settingRow}>
                    <Text style={styles.settingLabel}>Giriş Ücreti:</Text>
                    <Text style={styles.settingValue}>{formatCurrency(parkSettings.base_price, 'TL', 0)}</Text>
                  </View>
                  
                  <View style={styles.settingRow}>
                    <Text style={styles.settingLabel}>Dakika Ücreti:</Text>
                    <Text style={styles.settingValue}>{formatCurrency(parkSettings.price_per_minute, 'TL', 0)}</Text>
                  </View>
                  
                  <Divider style={{marginVertical: 10}} />
                  
                  <View style={styles.infoContainer}>
                    <MaterialCommunityIcons name="information-outline" size={20} color="rgb(125, 76, 236)" />
                    <Text style={styles.infoText}>
                      Toplam ücret hesaplaması: Giriş Ücreti + (Dakika Ücreti × Kalınan Süre)
                    </Text>
                  </View>
                  
                  <Button 
                    mode="contained" 
                    icon="pencil" 
                    onPress={startEditing}
                    style={styles.editButton}
                    labelStyle={{color: 'white', fontWeight: 'bold'}}
                  >
                    <Text style={{color: 'white'}}>Düzenle</Text>
                  </Button>
                </>
              )}
            </View>
          ) : (
            <View style={styles.noDataContainer}>
              <MaterialCommunityIcons name="alert-circle-outline" size={40} color="rgb(125, 76, 236)" />
              <Text style={styles.noDataText}>Oyun parkı ayarları bulunamadı.</Text>
            </View>
          )}
        </Card.Content>
      </Card>
      
      <Card style={styles.infoCard}>
        <Card.Content>
          <View style={styles.infoHeader}>
            <MaterialCommunityIcons name="information-outline" size={24} color="rgb(125, 76, 236)" />
            <Text style={styles.infoTitle}>Fiyatlandırma Bilgisi</Text>
          </View>
          <Text style={styles.infoText}>• Giriş Ücreti: Park ziyaretinin başlangıcında alınan sabit ücret.</Text>
          <Text style={styles.infoText}>• Dakika Ücreti: Her dakika için hesaplanan ek ücret.</Text>
          <Text style={styles.infoText}>• Toplam Ücret: Giriş Ücreti + (Dakika Ücreti × Kalınan Süre)</Text>
        </Card.Content>
      </Card>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
    padding: 10,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: 'rgb(125, 76, 236)',
  },
  card: {
    marginBottom: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    borderRadius: 8,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  headerTitle: {
    marginLeft: 10,
    fontSize: 20,
    color: 'rgb(125, 76, 236)',
    fontWeight: 'bold',
  },
  divider: {
    marginBottom: 16,
  },
  settingsContainer: {
    padding: 10,
  },
  settingRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingHorizontal: 10,
  },
  inputRow: {
    marginBottom: 16,
  },
  settingLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    flex: 1,
    color: 'rgb(125, 76, 236)',
  },
  settingValue: {
    fontSize: 16,
    flex: 1,
    textAlign: 'right',
  },
  input: {
    marginTop: 5,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  button: {
    flex: 1,
    marginHorizontal: 5,
  },
  editButton: {
    marginTop: 20,
    backgroundColor: 'rgb(125, 76, 236)',
  },
  noDataContainer: {
    alignItems: 'center',
    padding: 20,
  },
  noDataText: {
    marginTop: 10,
    fontSize: 16,
    textAlign: 'center',
  },
  infoCard: {
    marginBottom: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    borderRadius: 8,
  },
  infoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  infoTitle: {
    marginLeft: 8,
    fontSize: 18,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  infoText: {
    fontSize: 14,
    marginLeft: 8,
    flex: 1,
  },
  infoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
});

export default ParkSettingsScreen;
